import React from "react";
import i18nPackage from "../../i18n/i18n";
import { BasicStep } from "../components/basic-step";
import styles from "./index.module.css";
import {
	BaseSubstepProps,
	BasicSubstep,
	BasicSubstepDescription,
	BasicSubstepHeader,
	BasicSubstepTitle,
	getBasicSubstepIcon,
} from "../components/basic-substep";
import Guide from "../components/guide";
import { Trans } from "react-i18next";
import { CoreButton } from "../../core";
import { Link, List, ListItem, ListItemText } from "@mui/material";

export function ManagedGuestSessionSubstep({ status, rootRef }: BaseSubstepProps) {
	const statusKey = status === "started" ? "unstarted" : status;
	return (
		<BasicSubstep ref={rootRef}>
			<BasicSubstepHeader
				title={i18nPackage.t(`beforeQuiz.assessmentSetup.managedGuestSessionCheck.title.${statusKey}`)}
				description={i18nPackage.t(`beforeQuiz.assessmentSetup.managedGuestSessionCheck.description.${statusKey}`)}
				status={status}
			/>
			{status === "failed" && <ManagedGuestSessionGuide />}
		</BasicSubstep>
	);
}

function ManagedGuestSessionGuide() {
	return (
		<Guide
			description={i18nPackage.t("beforeQuiz.assessmentSetup.managedGuestSessionCheck.guideDescription")}
			src="https://demo.video.yuja.com/V/Video?v=14648917&node=63114461&a=170820275"
			iframeTitle={i18nPackage.t("beforeQuiz.assessmentSetup.managedGuestSessionCheck.videoTitle")}
			title={i18nPackage.t("beforeQuiz.assessmentSetup.managedGuestSessionCheck.guideTitle")}
		/>
	);
}

type IncognitoSubstepProps = BaseSubstepProps & {
	onTryAgain: () => void;
};

export function IncognitoSubstep({ status, rootRef, onTryAgain }: IncognitoSubstepProps) {
	const statusKey = status === "started" ? "unstarted" : status;
	return (
		<BasicSubstep ref={rootRef}>
			<div className={styles.assessmentSetupButtonWrapper}>
				<BasicSubstepHeader
					title={i18nPackage.t(`beforeQuiz.assessmentSetup.incognitoCheck.title.${statusKey}`)}
					description={i18nPackage.t(`beforeQuiz.assessmentSetup.incognitoCheck.description.${statusKey}`)}
					status={status}
				/>
				{status === "failed" && (
					<CoreButton onClick={onTryAgain}>
						{i18nPackage.t("beforeQuiz.assessmentSetup.incognitoCheck.tryAgain")}
					</CoreButton>
				)}
			</div>
			{status === "failed" && <IncognitoGuide />}
		</BasicSubstep>
	);
}

function IncognitoGuide() {
	return (
		<Guide
			description={i18nPackage.t("beforeQuiz.assessmentSetup.incognitoCheck.guide")}
			src="https://demo.video.yuja.com/V/Video?v=14648679&node=63114039&a=151215311"
			iframeTitle={i18nPackage.t("beforeQuiz.assessmentSetup.incognitoCheck.videoTitle")}
			title={i18nPackage.t("beforeQuiz.assessmentSetup.incognitoCheck.guideTitle")}
		/>
	);
}

export function MultipleDisplaySubstep({ status, rootRef }: BaseSubstepProps) {
	const statusKey = status === "started" ? "unstarted" : status;
	return (
		<BasicSubstep ref={rootRef}>
			<BasicSubstepHeader
				title={i18nPackage.t(`beforeQuiz.assessmentSetup.oneScreenEnforcementMode.title.${statusKey}`)}
				description={i18nPackage.t(`beforeQuiz.assessmentSetup.oneScreenEnforcementMode.description.${statusKey}`)}
				status={status}
			/>
		</BasicSubstep>
	);
}

export function SidePanelSubstep({ status, rootRef }: BaseSubstepProps) {
	const statusKey = status === "started" ? "unstarted" : status;
	return (
		<BasicSubstep ref={rootRef}>
			<BasicSubstepHeader
				title={i18nPackage.t(`beforeQuiz.assessmentSetup.sidePanelCheck.title.${statusKey}`)}
				description={i18nPackage.t(`beforeQuiz.assessmentSetup.sidePanelCheck.description.${statusKey}`)}
				status={status}
			/>
		</BasicSubstep>
	);
}

export type BlockedExtensionItem = {
	name: string;
	id: string;
};

type BlockedExtensionSubstepProps = BaseSubstepProps & {
	blockedExtensions?: BlockedExtensionItem[];
};

export function BlockedExtensionSubstep({ status, rootRef, blockedExtensions }: BlockedExtensionSubstepProps) {
	const statusKey = status === "started" ? "unstarted" : status;
	const description =
		status === "failed" && blockedExtensions
			? blockedExtensions.length === 1
				? i18nPackage.t("beforeQuiz.assessmentSetup.extensionBlockMode.description.failedSingular")
				: i18nPackage.t("beforeQuiz.assessmentSetup.extensionBlockMode.description.failedPlural")
			: status === "completed"
				? i18nPackage.t("beforeQuiz.assessmentSetup.extensionBlockMode.description.completed")
				: i18nPackage.t("beforeQuiz.assessmentSetup.extensionBlockMode.description.unstarted");

	return (
		<BasicSubstep ref={rootRef}>
			<div className={styles.blockedExtensionContainer}>
				{getBasicSubstepIcon(status)}
				<div className={styles.blockedExtensionWrapper}>
					<BasicSubstepTitle
						title={i18nPackage.t(`beforeQuiz.assessmentSetup.extensionBlockMode.title.${statusKey}`)}
					/>
					<BasicSubstepDescription description={description} />
					{status === "failed" && blockedExtensions && blockedExtensions.length > 0 && (
						<List className={styles.blockExtensionList}>
							{blockedExtensions.map((extension) => {
								return (
									<ListItem key={extension.id}>
										<Link
											component={"button"}
											onClick={() => {
												window.open(
													`https://chromewebstore.google.com/detail/${extension.id}`,
													"_blank",
													"noopener,noreferrer",
												);
											}}
											aria-label={i18nPackage.t(
												"beforeQuiz.assessmentSetup.extensionBlockMode.disableExtensionAriaLabel",
												{ extensionName: extension.name },
											)}
											sx={{
												verticalAlign: "baseline",
											}}
										>
											{extension.name}
										</Link>
									</ListItem>
								);
							})}
						</List>
					)}
				</div>
			</div>
			{status === "failed" && <BlockedExtensionGuide />}
		</BasicSubstep>
	);
}

function BlockedExtensionGuide() {
	return (
		<Guide
			description={i18nPackage.t("beforeQuiz.assessmentSetup.extensionBlockMode.guideDescription")}
			src="https://demo.video.yuja.com/V/Video?v=14648659&node=63114016&a=25976182"
			iframeTitle={i18nPackage.t("beforeQuiz.assessmentSetup.extensionBlockMode.videoTitle")}
			title={i18nPackage.t("beforeQuiz.assessmentSetup.extensionBlockMode.guideTitle")}
		/>
	);
}

type CloseTabsSubstepProps = BaseSubstepProps & {
	onCloseTabs: () => void;
};

export function CloseTabsSubstep({ status, onCloseTabs, rootRef }: CloseTabsSubstepProps) {
	const statusKey = status === "started" ? "unstarted" : status;
	return (
		<BasicSubstep className={styles.assessmentSetupButtonWrapper} ref={rootRef}>
			<BasicSubstepHeader
				title={i18nPackage.t(`beforeQuiz.assessmentSetup.closeOpenTabs.title.${statusKey}`)}
				description={i18nPackage.t(`beforeQuiz.assessmentSetup.closeOpenTabs.description.${statusKey}`)}
				status={status}
			/>
			{status === "failed" && (
				<CoreButton onClick={onCloseTabs}>
					{i18nPackage.t("beforeQuiz.assessmentSetup.closeOpenTabs.closeAllTabsButton")}
				</CoreButton>
			)}
		</BasicSubstep>
	);
}

export function VirtualMachineSubstep({ status, rootRef }: BaseSubstepProps) {
	const statusKey = status === "started" ? "unstarted" : status;
	return (
		<BasicSubstep ref={rootRef}>
			<BasicSubstepHeader
				title={i18nPackage.t(`beforeQuiz.assessmentSetup.blockVirtualMachines.title.${statusKey}`)}
				description={i18nPackage.t(`beforeQuiz.assessmentSetup.blockVirtualMachines.description.${statusKey}`)}
				status={status}
			/>
		</BasicSubstep>
	);
}

export function RecordApplicationSubstep({ status, rootRef }: BaseSubstepProps) {
	const statusKey = status === "completed" ? status : "unstarted";
	return (
		<BasicSubstep ref={rootRef}>
			<BasicSubstepHeader
				title={i18nPackage.t(`beforeQuiz.assessmentSetup.recordApplicationsActivity.title.${statusKey}`)}
				description={i18nPackage.t(`beforeQuiz.assessmentSetup.recordApplicationsActivity.description.${statusKey}`)}
				status={status}
			/>
		</BasicSubstep>
	);
}

type BlockApplicationSubstepProps = BaseSubstepProps & {
	onForceQuit: () => void;
	applicationNames?: string[];
};

export function BlockApplicationSubstep({
	status,
	onForceQuit,
	applicationNames,
	rootRef,
}: BlockApplicationSubstepProps) {
	const statusKey = status === "started" ? "unstarted" : status;

	return (
		<BasicSubstep ref={rootRef}>
			<div className={styles.blockApplicationWrapper}>
				<div className={styles.blockApplicationRowWrapper}>
					{getBasicSubstepIcon(status)}
					<div className={styles.blockApplicationTextWrapper}>
						<BasicSubstepTitle
							title={i18nPackage.t(`beforeQuiz.assessmentSetup.applicationBlockMode.title.${statusKey}`)}
						/>
						<BasicSubstepDescription
							description={
								<Trans
									i18n={i18nPackage}
									i18nKey={`beforeQuiz.assessmentSetup.applicationBlockMode.description.${statusKey}`}
									components={{ b: <strong /> }}
								/>
							}
						/>
						{applicationNames && status === "failed" && (
							<List className={styles.blockApplicationList}>
								{applicationNames.map((application) => (
									<ListItem key={application}>
										<ListItemText primary={application} />
									</ListItem>
								))}
							</List>
						)}
					</div>
				</div>
				{status === "failed" && (
					<CoreButton onClick={onForceQuit} className={styles.forceQuit}>
						{i18nPackage.t("beforeQuiz.assessmentSetup.applicationBlockMode.forceQuitButton")}
					</CoreButton>
				)}
			</div>
			{status === "failed" && <BlockApplicationGuide />}
		</BasicSubstep>
	);
}

function BlockApplicationGuide({ ref }: { ref?: React.Ref<HTMLDivElement> }) {
	return (
		<Guide
			description={i18nPackage.t("beforeQuiz.assessmentSetup.applicationBlockMode.guide")}
			src="https://demo.video.yuja.com/V/Video?v=14648654&node=63114011&a=63390952"
			iframeTitle={i18nPackage.t("beforeQuiz.assessmentSetup.applicationBlockMode.videoTitle")}
			ref={ref}
			title={i18nPackage.t("beforeQuiz.assessmentSetup.applicationBlockMode.guideTitle")}
		/>
	);
}

export default function AssessmentSetupStep({ children }: { children: React.ReactNode }) {
	return <BasicStep description={i18nPackage.t("beforeQuiz.assessmentSetup.description")}>{children}</BasicStep>;
}
